/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ExtensionStatusPage.tsx
 🗓️ Created: 2025-12-10
 🧩 Description:
    Extension Status page - displays extensions with call forwarding status.
 ────────────────────────────────────────────────────────────────*/

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    PhoneForwarded,
    RefreshCw,
    Phone,
    Edit2,
    Circle,
    CheckCircle2,
} from 'lucide-react';
import { ModulePageContainer } from '../../components/layouts';
import DataGridSimple from '../../components/ui/DataGridSimple';
import FixedActionBar from '../../components/ui/FixedActionBar';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import {
    fetchExtensionStatus,
    toggleForwarding,
    ExtensionStatusEntry,
    CallForwardingStatus,
} from './api';
import DevicesModal from './DevicesModal';
import EditStatusModal from './EditStatusModal';

interface ColumnDef {
    key: string;
    label: string;
    visible: boolean;
    sortable?: boolean;
    render?: (value: unknown, item: ExtensionStatusEntry) => React.ReactNode;
}

// Status indicator component
const StatusIndicator = ({
    enabled,
    onClick,
    loading
}: {
    enabled: boolean;
    onClick?: () => void;
    loading?: boolean;
}) => {
    const baseClass = "w-5 h-5 cursor-pointer transition-all duration-200 hover:scale-125";

    if (loading) {
        return <RefreshCw className={`${baseClass} animate-spin text-gray-400`} size={16} />;
    }

    if (enabled) {
        return (
            <CheckCircle2
                className={`${baseClass} text-green-500 hover:text-green-600`}
                size={16}
                onClick={onClick}
            />
        );
    }

    return (
        <Circle
            className={`${baseClass} text-gray-300 hover:text-gray-400`}
            size={16}
            onClick={onClick}
        />
    );
};

export default function ExtensionStatusPage() {
    const { t } = useTranslation();
    const { showError, showSuccess } = useNotifications();
    const { selectedDomain } = useContextStore();

    // State
    const [data, setData] = useState<ExtensionStatusEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [search, setSearch] = useState('');
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Modals
    const [devicesModalOpen, setDevicesModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedExtension, setSelectedExtension] = useState<ExtensionStatusEntry | null>(null);

    // Toggle loading states
    const [togglingStates, setTogglingStates] = useState<Record<string, boolean>>({});

    // Column visibility state
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({
        extension: true,
        bossSecretary: true,
        personalAssistant: true,
        followMe: true,
        dnd: true,
        cfi: true,
        cfb: true,
        cfn: true,
        cfu: true,
        callCompletion: true,
        actions: true,
    });

    // Load data
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const result = await fetchExtensionStatus({
                page,
                limit,
                search,
                domainId: selectedDomain?.id
            });
            setData(result.data || []);
            if (result.pagination) {
                setTotal(result.pagination.total);
                setTotalPages(result.pagination.totalPages);
            }
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Failed to load extension status');
        } finally {
            setLoading(false);
        }
    }, [page, limit, search, selectedDomain?.id, showError]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Handle toggle
    const handleToggle = useCallback(async (
        extensionIdentifier: number | string,
        forwardType: keyof CallForwardingStatus,
        currentValue: boolean
    ) => {
        const key = `${extensionIdentifier}-${forwardType}`;
        setTogglingStates(prev => ({ ...prev, [key]: true }));

        try {
            await toggleForwarding(extensionIdentifier, forwardType, !currentValue);

            // Update local state
            setData(prev => prev.map(ext => {
                if ((ext.uuid || ext.id) === extensionIdentifier) {
                    return {
                        ...ext,
                        forwarding: {
                            ...ext.forwarding,
                            [forwardType]: !currentValue,
                        },
                    };
                }
                return ext;
            }));

            showSuccess(t('extensionStatus.toggleSuccess', 'Status updated'));
        } catch (error) {
            showError(error instanceof Error ? error.message : 'Failed to update status');
        } finally {
            setTogglingStates(prev => ({ ...prev, [key]: false }));
        }
    }, [showSuccess, showError, t]);

    // Open devices modal
    const handleOpenDevices = useCallback((ext: ExtensionStatusEntry) => {
        setSelectedExtension(ext);
        setDevicesModalOpen(true);
    }, []);

    // Open edit modal
    const handleOpenEdit = useCallback((ext: ExtensionStatusEntry) => {
        setSelectedExtension(ext);
        setEditModalOpen(true);
    }, []);

    // Handle column toggle
    const handleColumnToggle = useCallback((columnKey: string) => {
        setColumnVisibility(prev => ({
            ...prev,
            [columnKey]: !prev[columnKey],
        }));
    }, []);

    // Build columns array with current visibility
    const columns: ColumnDef[] = [
        {
            key: 'extension',
            label: t('extensionStatus.extension', 'Extension'),
            visible: columnVisibility.extension,
            render: (_: unknown, item: ExtensionStatusEntry) => (
                <span className="font-medium text-foreground whitespace-nowrap">
                    {item.extension}{item.name ? ` - ${item.name}` : ''}
                </span>
            ),
        },
        {
            key: 'bossSecretary',
            label: t('extensionStatus.bossSecretary', 'Boss/Secretary'),
            visible: columnVisibility.bossSecretary,
            render: (_: unknown, item: ExtensionStatusEntry) => (
                <StatusIndicator
                    enabled={item.forwarding.bossSecretary}
                    loading={togglingStates[`${item.uuid || item.id}-bossSecretary`]}
                    onClick={() => handleToggle(item.uuid || item.id, 'bossSecretary', item.forwarding.bossSecretary)}
                />
            ),
        },
        {
            key: 'personalAssistant',
            label: t('extensionStatus.personalAssistant', 'Personal Assistant'),
            visible: columnVisibility.personalAssistant,
            render: (_: unknown, item: ExtensionStatusEntry) => (
                <StatusIndicator
                    enabled={item.forwarding.personalAssistant}
                    loading={togglingStates[`${item.uuid || item.id}-personalAssistant`]}
                    onClick={() => handleToggle(item.uuid || item.id, 'personalAssistant', item.forwarding.personalAssistant)}
                />
            ),
        },
        {
            key: 'followMe',
            label: t('extensionStatus.followMe', 'Follow-me'),
            visible: columnVisibility.followMe,
            render: (_: unknown, item: ExtensionStatusEntry) => (
                <StatusIndicator
                    enabled={item.forwarding.followMe}
                    loading={togglingStates[`${item.uuid || item.id}-followMe`]}
                    onClick={() => handleToggle(item.uuid || item.id, 'followMe', item.forwarding.followMe)}
                />
            ),
        },
        {
            key: 'dnd',
            label: t('extensionStatus.dnd', 'DND'),
            visible: columnVisibility.dnd,
            render: (_: unknown, item: ExtensionStatusEntry) => (
                <StatusIndicator
                    enabled={item.forwarding.dnd}
                    loading={togglingStates[`${item.uuid || item.id}-dnd`]}
                    onClick={() => handleToggle(item.uuid || item.id, 'dnd', item.forwarding.dnd)}
                />
            ),
        },
        {
            key: 'cfi',
            label: t('extensionStatus.cfi', 'CFI'),
            visible: columnVisibility.cfi,
            render: (_: unknown, item: ExtensionStatusEntry) => (
                <StatusIndicator
                    enabled={item.forwarding.cfi}
                    loading={togglingStates[`${item.uuid || item.id}-cfi`]}
                    onClick={() => handleToggle(item.uuid || item.id, 'cfi', item.forwarding.cfi)}
                />
            ),
        },
        {
            key: 'cfb',
            label: t('extensionStatus.cfb', 'CFB'),
            visible: columnVisibility.cfb,
            render: (_: unknown, item: ExtensionStatusEntry) => (
                <StatusIndicator
                    enabled={item.forwarding.cfb}
                    loading={togglingStates[`${item.uuid || item.id}-cfb`]}
                    onClick={() => handleToggle(item.uuid || item.id, 'cfb', item.forwarding.cfb)}
                />
            ),
        },
        {
            key: 'cfn',
            label: t('extensionStatus.cfn', 'CFN'),
            visible: columnVisibility.cfn,
            render: (_: unknown, item: ExtensionStatusEntry) => (
                <StatusIndicator
                    enabled={item.forwarding.cfn}
                    loading={togglingStates[`${item.uuid || item.id}-cfn`]}
                    onClick={() => handleToggle(item.uuid || item.id, 'cfn', item.forwarding.cfn)}
                />
            ),
        },
        {
            key: 'cfu',
            label: t('extensionStatus.cfu', 'CFU'),
            visible: columnVisibility.cfu,
            render: (_: unknown, item: ExtensionStatusEntry) => (
                <StatusIndicator
                    enabled={item.forwarding.cfu}
                    loading={togglingStates[`${item.uuid || item.id}-cfu`]}
                    onClick={() => handleToggle(item.uuid || item.id, 'cfu', item.forwarding.cfu)}
                />
            ),
        },
        {
            key: 'callCompletion',
            label: t('extensionStatus.callCompletion', 'Call Completion'),
            visible: columnVisibility.callCompletion,
            render: (_: unknown, item: ExtensionStatusEntry) => (
                <StatusIndicator
                    enabled={item.forwarding.callCompletion}
                    loading={togglingStates[`${item.uuid || item.id}-callCompletion`]}
                    onClick={() => handleToggle(item.uuid || item.id, 'callCompletion', item.forwarding.callCompletion)}
                />
            ),
        },
        {
            key: 'actions',
            label: t('common.actions', 'Actions'),
            visible: columnVisibility.actions,
            sortable: false,
        },
    ];

    return (
        <ModulePageContainer
            currentView="list"
            listView={
                <div className="pb-20">
                    <DataGridSimple
                        data={data}
                        columns={columns}
                        loading={loading}
                        pagination={{
                            page,
                            limit,
                            total,
                            totalPages,
                        }}
                        searchQuery={search}
                        onSearch={setSearch}
                        onPageChange={setPage}
                        onItemsPerPageChange={(newLimit) => {
                            setLimit(newLimit);
                            setPage(1);
                        }}
                        onColumnToggle={handleColumnToggle}
                        onSort={() => { }}
                        sortBy="extension"
                        sortOrder="asc"
                        title={t('extensionStatus.title', 'Extension Status')}
                        titleIcon={<PhoneForwarded className="h-5 w-5 text-primary" />}
                        renderActions={(item: ExtensionStatusEntry) => (
                            <div className="flex items-center gap-2">
                                <SimpleTooltip content={t('extensionStatus.devices', 'Devices')}>
                                    <button
                                        onClick={() => handleOpenDevices(item)}
                                        className="p-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors"
                                    >
                                        <Phone size={16} />
                                    </button>
                                </SimpleTooltip>
                                <SimpleTooltip content={t('common.edit', 'Edit')}>
                                    <button
                                        onClick={() => handleOpenEdit(item)}
                                        className="p-2 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground transition-colors"
                                    >
                                        <Edit2 size={16} />
                                    </button>
                                </SimpleTooltip>
                            </div>
                        )}
                    />
                </div>
            }
            formView={null}
        >
            <FixedActionBar
                showSave={false}
                showCancel={false}
                additionalButtons={[
                    {
                        key: 'refresh',
                        label: t('common.refresh', 'Refresh'),
                        icon: <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />,
                        onClick: loadData,
                        variant: 'default',
                    },
                ]}
            />

            {/* Devices Modal */}
            {selectedExtension && (
                <DevicesModal
                    open={devicesModalOpen}
                    onClose={() => {
                        setDevicesModalOpen(false);
                        setSelectedExtension(null);
                    }}
                    extension={selectedExtension}
                />
            )}

            {/* Edit Status Modal */}
            {selectedExtension && (
                <EditStatusModal
                    open={editModalOpen}
                    onClose={() => {
                        setEditModalOpen(false);
                        setSelectedExtension(null);
                    }}
                    extension={selectedExtension}
                    onSave={() => {
                        loadData();
                        setEditModalOpen(false);
                        setSelectedExtension(null);
                    }}
                />
            )}
        </ModulePageContainer>
    );
}
